import React, { useState, useEffect } from 'react'; 
import { useTranslation } from 'react-i18next';     
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotifications, Notification } from '../../hooks/useNotifications';
import { BellIcon } from '@heroicons/react/24/outline';
import { BellIcon as BellIconSolid } from '@heroicons/react/24/solid';

interface NotificationBellProps {
  className?: string;
  onClick?: () => void;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ className = '', onClick }) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const { unreadCount, fetchUnreadCount } = useNotifications();
  const [isHovered, setIsHovered] = useState(false);

  // Refresh unread count when component mounts
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <button
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`relative p-2 text-gray-600 hover:text-emerald-600 transition-colors duration-200 ${className}`}
      aria-label={t('notifications.title', 'Notifications')}
    >
      {/* Bell Icon */}
      <div className="relative">
        {unreadCount > 0 ? (
          <BellIconSolid className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600" />
        ) : (
          <BellIcon className="h-5 w-5 sm:h-6 sm:w-6" />
        )}
        
        {/* Unread Count Badge */}
        {unreadCount > 0 && (
          <span className={`absolute -top-1 ${isRTL ? '-left-1' : '-right-1'} min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1 animate-pulse`}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </div>

      {/* Tooltip */}
      <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} top-full mt-2 px-2 py-1 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 transition-opacity duration-200 pointer-events-none ${
        isHovered ? 'opacity-100' : ''
      }`}>
        {unreadCount > 0 
          ? t('notifications.unreadCount', `{{count}} unread notifications`, { count: unreadCount })
          : t('notifications.noUnread', 'No unread notifications')
        }
        <div className={`absolute ${isRTL ? 'left-2' : 'right-2'} -top-1 w-2 h-2 bg-gray-800 transform rotate-45`} />
      </div>
    </button>
  );
};

export default NotificationBell;
